import React from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ChartUserByCountry from './ChartUserByCountry';
import CustomizedDataGrid from './CustomizedDataGrid';
import PageViewsBarChart from './PageViewsBarChart';
import StatCard from './StatCard';
import EvGrowth from './EvGrowth';
import ElectricCarTwoToneIcon from '@mui/icons-material/ElectricCarTwoTone';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import MilitaryTechOutlinedIcon from '@mui/icons-material/MilitaryTechOutlined';
import BatteryChargingFullOutlinedIcon from '@mui/icons-material/BatteryChargingFullOutlined';
import BatteryRange from './BatteryRange';
import MakeModel from './MakeModel';
import AnnualCount from './AnnualCount';
import TopLocations from './TopLocations';

const MainGrid = ({ section = 'overview', searchQuery = '', loading, error, metrics, chartData, onRetry }) => {
  const [tableFilterPreset, setTableFilterPreset] = React.useState('all');

  const sectionMeta = {
    overview: {
      heading: 'Overview',
      subheading: 'Executive snapshot of EV adoption, growth and composition.',
    },
    charging: {
      heading: 'Charging Insights',
      subheading: 'Battery behavior and EV type mix to understand charging demand patterns.',
    },
    statistics: {
      heading: 'Advanced Statistics',
      subheading: 'Trend and distribution deep-dive across years, makes and models.',
    },
    maintenance: {
      heading: 'Maintenance Center',
      subheading: 'Operational monitoring with detailed records and regional concentration.',
    },
    settings: {
      heading: 'Dashboard Settings',
      subheading: 'Configuration-oriented view with core KPI and supporting datasets.',
    },
  };

  const activeSection = sectionMeta[section] ? section : 'overview';

  const data = [
    {
      title: 'Total EVs',
      value: metrics?.totalEVs?.toLocaleString() || '',
      icon: <ElectricCarTwoToneIcon fontSize='medium' />,
    },
    {
      title: 'Top Manufacturer',
      value: metrics?.topManufacturer || '',
      icon: <FactoryOutlinedIcon fontSize='large' />,
    },
    {
      title: 'Popular Model',
      value: metrics?.topModel || '',
      icon: <MilitaryTechOutlinedIcon fontSize='large' />,
    },
    {
      title: 'Avg Battery Range',
      value: metrics?.avgBatteryRange ? `${Math.round(metrics.avgBatteryRange)} Miles` : '',
      icon: <BatteryChargingFullOutlinedIcon fontSize='medium' />,
    },
  ];


  if (error) {
    return (
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!loading && !chartData) {
    return (
      <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
        <Alert severity="info">No EV records are available to display.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '1700px' } }}>
      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        {sectionMeta[activeSection].heading}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
        {sectionMeta[activeSection].subheading}
      </Typography>

      <Grid container spacing={2} columns={12} sx={{ mb: (theme) => theme.spacing(2) }}>
        {data.map((card, index) => (
          <Grid key={index} size={{ xs: 12, sm: 6, lg: 3 }}>
            <StatCard {...card} />
          </Grid>
        ))}

        {activeSection === 'overview' && (
          <React.Fragment>
            <Grid size={{ xs: 12, md: 6 }}>
              <AnnualCount chartData={chartData?.modelYearDistribution} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PageViewsBarChart chartData={chartData?.topManufacturers} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <EvGrowth chartData={chartData?.evGrowthOverTime} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MakeModel chartData={chartData?.makeModelDistribution} loading={loading} />
            </Grid>
          </React.Fragment>
        )}

        {activeSection === 'charging' && (
          <React.Fragment>
            <Grid size={{ xs: 12, md: 6 }}>
              <BatteryRange chartData={chartData?.batteryRangeDistribution} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartUserByCountry chartData={chartData?.evTypesDistribution} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <AnnualCount chartData={chartData?.modelYearDistribution} loading={loading} />
            </Grid>
          </React.Fragment>
        )}

        {activeSection === 'statistics' && (
          <React.Fragment>
            <Grid size={{ xs: 12, md: 6 }}>
              <AnnualCount chartData={chartData?.modelYearDistribution} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <EvGrowth chartData={chartData?.evGrowthOverTime} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PageViewsBarChart chartData={chartData?.topManufacturers} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <MakeModel chartData={chartData?.makeModelDistribution} loading={loading} />
            </Grid>
          </React.Fragment>
        )}

        {activeSection === 'maintenance' && (
          <React.Fragment>
            <Grid size={{ xs: 12, md: 6 }}>
              <TopLocations chartData={chartData?.locationDistribution} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartUserByCountry chartData={chartData?.evTypesDistribution} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <PageViewsBarChart chartData={chartData?.topManufacturers} loading={loading} />
            </Grid>
          </React.Fragment>
        )}

        {activeSection === 'settings' && (
          <React.Fragment>
            <Grid size={{ xs: 12, md: 6 }}>
              <ChartUserByCountry chartData={chartData?.evTypesDistribution} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <BatteryRange chartData={chartData?.batteryRangeDistribution} loading={loading} />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TopLocations chartData={chartData?.locationDistribution} loading={loading} />
            </Grid>
          </React.Fragment>
        )}
      </Grid>

      <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
        Records
      </Typography>
      <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', rowGap: 1 }}>
        <Chip
          label="All"
          clickable
          color={tableFilterPreset === 'all' ? 'secondary' : 'default'}
          onClick={() => setTableFilterPreset('all')}
        />
        <Chip
          label="BEV Only"
          clickable
          color={tableFilterPreset === 'bevOnly' ? 'secondary' : 'default'}
          onClick={() => setTableFilterPreset('bevOnly')}
        />
        <Chip
          label="Model Year >= 2020"
          clickable
          color={tableFilterPreset === 'year2020' ? 'secondary' : 'default'}
          onClick={() => setTableFilterPreset('year2020')}
        />
        <Chip
          label="Range >= 200 mi"
          clickable
          color={tableFilterPreset === 'range200' ? 'secondary' : 'default'}
          onClick={() => setTableFilterPreset('range200')}
        />
      </Stack>
      <Grid container spacing={2} columns={12}>
        <Grid size={{ xs: 12, lg: 9 }}>
          <CustomizedDataGrid
            loading={loading}
            chartData={chartData?.tableData}
            searchQuery={searchQuery}
            filterPreset={tableFilterPreset}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <Stack gap={2} direction={{ xs: 'column', sm: 'row', lg: 'column' }}>
            <BatteryRange chartData={chartData?.batteryRangeDistribution} loading={loading} />
            <TopLocations chartData={chartData?.locationDistribution} loading={loading} />
          </Stack>
        </Grid>
      </Grid>

    </Box>
  );
};

export default MainGrid;